// components/portal/BrandRevenueChart.tsx
//
// Revenue graph for the brand detail "Revenue" tab. It plots FILED FIGURES AND NOTHING ELSE.
//
// WHAT WAS HERE BEFORE, and why it is gone: the chart took the brand's single revenue number and
// back-projected it over four years at its growth rate - or, when there was no growth rate, at a
// hard-coded 5% a year. On the brand that started this, both inputs were wrong: the figure was an
// unrelated company's turnover and there was no growth rate at all, so the entire rising curve was
// invented, four points drawn from one number and a constant. It carried a 10px grey "modeled"
// label, which is not a defence: nobody reads a caption over a chart.
//
// A chart is the most credible object on a page. One real point renders as one point, and no
// points renders as the reason there are none.
'use client';

import React, { useMemo, useState } from 'react';
import { ResponsiveLine } from '@nivo/line';
import { ChartErrorBoundary } from '@/components/ui/ChartErrorBoundary';
import { useKernelStore } from '@/kernel/kernelStores';
import { pickDeclaredSeries } from '@/components/portal/declaredSeries';
import { formatRevenue } from '#lib/leasing/enrichNote';

const PANEL_FONT = 'ui-sans-serif, -apple-system, system-ui, sans-serif';
const LINE_COLOR = '#5B8DEF';

// Hoisted out of the JSX: Nivo keys its internal scale/path/theme useMemo on prop identity, so a
// literal rebuilt on every render recomputes the whole chart for nothing (MAINT_03).
const LINE_COLORS = [LINE_COLOR];
// The claim's grammar: grey where the register's line is blue, dashed where it is solid.
const DECLARED_LINE_COLOR = '#8A8A8A';
const DECLARED_LINE_COLORS = [DECLARED_LINE_COLOR];
const CHART_MARGIN = { top: 20, right: 20, bottom: 28, left: 54 } as const;
const X_SCALE = { type: 'point' } as const;
const Y_SCALE = { type: 'linear', min: 0, max: 'auto' } as const;
// A loss year goes below zero.
const PROFIT_Y_SCALE = { type: 'linear', min: 'auto', max: 'auto' } as const;
const AXIS_BOTTOM = { tickSize: 0, tickPadding: 8 } as const;
// Currency rides in from the drawn series (EDGAR group filings are USD): a fixed formatter
// would print an audited $1.5B as "€1.5B", which is a different number, not a style choice.
// A loss is a figure too: formatRevenue only knows positive amounts.
const signedRevenue = (v: number, currency: string) => (v < 0 ? `−${formatRevenue(-v, currency) ?? ''}` : formatRevenue(v, currency));
const axisLeftFor = (currency: string) => ({
  tickSize: 0,
  tickPadding: 6,
  tickValues: 4,
  format: (v: string | number) => signedRevenue(Number(v), currency) ?? String(v),
} as const);
const CHART_THEME = {
  axis: { ticks: { text: { fill: '#6E6E6E', fontSize: 10, fontFamily: PANEL_FONT } } },
  grid: { line: { stroke: '#232323', strokeWidth: 1 } },
  tooltip: { container: { background: '#353535', color: '#E8E8E8', fontSize: 11, borderRadius: 6, border: '1px solid #2A2A2A' } },
} as const;
const TOOLTIP_STYLE = { ...CHART_THEME.tooltip.container, padding: '4px 8px' } as const;

const percent = (v: number | null) => (v === null || !Number.isFinite(v) ? '–' : `${v >= 0 ? '+' : '−'}${Math.abs(v * 100).toFixed(1)}%`);
const LABEL = { fontSize: 12 } as const;
const VALUE = { fontSize: 20, lineHeight: '26px' } as const;
const CELL = { fontSize: 13 } as const;

/** One headline figure: label over value, ChatGPT's quiet stat. */
const Stat = ({ label, value, note }: { label: string; value: string; note?: string }) => (
  <div className="min-w-0 rounded-[16px] border border-[#262626] bg-[#1B1B1B] px-4 py-3">
    <div className="truncate text-[#8E8E8E]" style={LABEL}>{label}</div>
    <div className="mt-1 truncate font-medium text-[#ECECEC]" style={VALUE}>{value}</div>
    {note ? <div className="mt-0.5 truncate text-[#8E8E8E]" style={LABEL}>{note}</div> : null}
  </div>
);

const revenueTooltipFor = (currency: string): React.ComponentProps<typeof ResponsiveLine>['tooltip'] =>
  function RevenueTooltip({ point }) {
    return (
      <div style={TOOLTIP_STYLE}>
        {point.data.xFormatted}: {signedRevenue(Number(point.data.y), currency)}
      </div>
    );
  };

// Takes the brand ID, not the brand: what it draws is read from the registry with atomic
// selectors, so a change on any OTHER Brand field does not re-render the chart, and no caller has
// to subscribe to the whole record on its behalf (MAINT_02).

// THE DECLARED CURVE. "Il nous faut toujours une courbe" (owner, 2026-09-19): when no register
// (rule + grouping: components/portal/declaredSeries.ts)
// we integrate can verify a company's accounts, the agent records the official series it
// researched, and THAT draws - dashed, grey, headed by its source - instead of a blank panel.
// The grouping rule is the honesty: a line may only join years that share SOURCE and CURRENCY.
// L'Orange Bleue's two press claims (10M and 169M, different perimeters, different papers) must
// never be joined - each is a row in the list, no line. A dashed curve is a cited claim, drawn;
// the solid curve stays reserved for figures the machine itself re-read from a register.

/** Nivo's stock line, redrawn dashed: the visual grammar for "claimed, not proved". */
const DashedLineLayer = ({ series, lineGenerator }: any) => (
  <>
    {series.map(({ id, data, color }: any) => (
      <path
        key={String(id)}
        d={lineGenerator(data.map((d: any) => d.position)) ?? undefined}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeDasharray="6 5"
      />
    ))}
  </>
);
// The stock layer stack with 'lines' swapped for the dashed drawing; everything else stays Nivo's.
const DECLARED_LAYERS = ['grid', 'axes', 'areas', DashedLineLayer, 'points', 'mesh'] as React.ComponentProps<typeof ResponsiveLine>['layers'];

// THE MIDDLE TIER: figures we hold with a citation but no verdict. They are information the
// verified chart is not allowed to carry, and hiding them entirely produced the worst version of
// honesty: a blank panel beside an agent conversation quoting those very numbers (L'Orange Bleue,
// 2026-09-19 - franchisor accounts and network volume found and cited, panel empty). Each row is
// served AS a claim: amount, year, the source by name as a link, and an unmissable label. It
// never enters the chart, a sort, or a scoring judge - rendering is all it does.
const DeclaredFinancials = ({ brandId, lang }: { brandId?: string; lang: string }) => {
  const fr = lang === 'fr';
  const rows = useKernelStore((state) =>
    brandId ? state.inventory.Brand.registry[brandId]?.declaredFinancials ?? null : null
  );
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-3 max-w-3xl rounded-[20px] border border-[#262626] bg-[#1B1B1B]">
      <div className="flex items-center justify-between px-5 pb-1 pt-4">
        <span className="font-medium text-white" style={{ fontSize: 14 }}>
          {fr ? 'Déclaré, non vérifié' : 'Declared, unverified'}
        </span>
        <span className="text-[#8E8E8E]" style={{ fontSize: 12 }}>
          {fr ? 'tel que publié par la source citée' : 'as published by the cited source'}
        </span>
      </div>
      <div className="px-4 py-2">
        {rows.map((r) => (
          <div key={`${r.year}-${r.source ?? ''}`} className="flex items-center justify-between py-1.5">
            <span className="text-[12px] text-[#E8E8E8]">
              {r.year} · {formatRevenue(r.revenue, r.currency)}
            </span>
            <span className="text-[#8E8E8E]" style={{ fontSize: 12 }}>
              {r.sourceUrl ? (
                <a href={r.sourceUrl} target="_blank" rel="noreferrer" className="underline decoration-[#3A3A3A] underline-offset-2 hover:text-[#9A9A9A]">
                  {r.source || (fr ? 'source' : 'source')}
                </a>
              ) : (
                r.source || (fr ? 'source non conservée' : 'source not kept')
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};


// GROUP OR WORLDWIDE REVENUE, VERIFIED (2026-10-04): the brand's whole company or its parent group,
// checked on its page or read from a foreign register. Never mixed into the French series; shown on
// its own, named and in its own currency, so a brand with no French filing still shows its scale
// (Jacquemus EUR 102m -> 280m 2021-2023; Uniqlo's Fast Retailing).
const GroupRevenue = ({ brandId, lang }: { brandId?: string; lang: string }) => {
  const fr = lang === 'fr';
  const rows = useKernelStore((state) => (brandId ? state.inventory.Brand.registry[brandId]?.groupFinancials ?? null : null));
  const series = useMemo(() => {
    const byKey = new Map<string, NonNullable<typeof rows>>();
    for (const r of rows ?? []) { const key = `${r.entityName ?? ''}|${r.currency}`; byKey.set(key, [...(byKey.get(key) ?? []), r]); }
    const best = [...byKey.values()].sort((a, b) => b.length - a.length)[0];
    if (!best) return null;
    const byYear = new Map(best.map((r) => [r.year, r]));
    const list = [...byYear.values()].sort((a, b) => a.year - b.year);
    return { list, currency: list[0]!.currency, entityName: list[0]!.entityName, data: [{ id: 'group', data: list.map((r) => ({ x: String(r.year), y: r.revenue })) }] };
  }, [rows]);
  const axisLeft = useMemo(() => (series ? axisLeftFor(series.currency) : null), [series]);
  const tooltip = useMemo(() => (series ? revenueTooltipFor(series.currency) : null), [series]);
  if (!series || !axisLeft || !tooltip) return null;
  const name = series.entityName ?? (fr ? 'groupe' : 'group');
  return (
    <div className="mt-3 max-w-3xl overflow-hidden rounded-[20px] border border-[#262626] bg-[#1B1B1B]">
      <div className="flex items-center justify-between gap-3 px-5 pb-1 pt-4">
        <span className="font-medium text-white" style={{ fontSize: 14 }}>{fr ? 'Chiffre d’affaires du groupe ou mondial' : 'Group or worldwide revenue'}</span>
        <span className="truncate text-[#8E8E8E]" style={{ fontSize: 12 }}>
          {fr ? `vérifié · ${name} · ${series.currency}` : `verified · ${name} · ${series.currency}`}
        </span>
      </div>
      {series.list.length >= 2 ? (
        <div className="h-[240px] w-full p-2">
          <ChartErrorBoundary label="Group revenue">
            <ResponsiveLine data={series.data} margin={CHART_MARGIN} xScale={X_SCALE} yScale={Y_SCALE} curve="monotoneX" colors={LINE_COLORS} lineWidth={2}
              enableArea areaOpacity={0.12} enablePoints pointSize={7} pointColor="#141414" pointBorderWidth={2} pointBorderColor={LINE_COLOR}
              enableGridX={false} gridYValues={4} axisBottom={AXIS_BOTTOM} axisLeft={axisLeft} isInteractive useMesh theme={CHART_THEME} tooltip={tooltip} />
          </ChartErrorBoundary>
        </div>
      ) : null}
      <div className="px-4 pb-3 pt-1">
        {[...series.list].reverse().map((r) => (
          <div key={r.year} className="flex items-center justify-between border-t border-[#262626] py-1.5">
            <span className="text-[12px] text-[#E8E8E8]">{r.year} · {formatRevenue(r.revenue, r.currency)}</span>
            {r.sourceUrl ? (
              <a href={r.sourceUrl} target="_blank" rel="noreferrer" className="text-[#8E8E8E] underline decoration-[#3A3A3A] underline-offset-2 hover:text-[#9A9A9A]" style={{ fontSize: 12 }}>
                source
              </a>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
};

// WHY NO RECENT ACCOUNTS (owner, 2026-10-04: "produce an investigation and add it as a justification
// in the revenue card"). The official gazette and directory say whether the company files under the
// legal confidentiality declaration, is too young to have filed twice, or does not file at all;
// the investigator's finding, when written, says who owns it and where figures exist instead.
const longDate = (iso: string, fr: boolean) => new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString(fr ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const RegisterAccountsNotice = ({ brandId, lang }: { brandId?: string; lang: string }) => {
  const fr = lang === 'fr';
  const accounts = useKernelStore((state) => (brandId ? state.inventory.Brand.registry[brandId]?.registerAccounts ?? null : null));
  const drawnYears = useKernelStore((state) => (brandId ? (state.inventory.Brand.registry[brandId]?.verifiedFinancials ?? []).filter((r) => (r.revenue ?? 0) > 0).length : 0));
  // A mixed register (one year read, one not) only needs saying when the card has no series to show.
  if (!accounts || (accounts.kind === 'partial' && drawnYears >= 2)) return null;
  const main = accounts.companies.find((c) => !c.parent) ?? accounts.companies[0];
  const name = main?.name ?? (fr ? 'La société' : 'The company');
  const lead = accounts.kind === 'not_filing'
    ? fr
      ? `${name} ne dépose pas ses comptes, que la loi impose chaque année (${accounts.lastFiled ? `dernier exercice déposé : ${accounts.lastFiled}` : 'jamais déposés'}).`
      : `${name} does not file its accounts, which the law requires every year (${accounts.lastFiled ? `last filed for ${accounts.lastFiled}` : 'never filed'}).`
    : accounts.kind === 'partial'
      // A ready brand can rest on this mix (one year read, the other withheld or not filed): the card says
      // so year by year instead of showing nothing (APOLLO SPORTING CLUB, 2026-10-04).
      ? `${fr ? 'Registre, deux derniers exercices :' : 'Register, last two years:'} ${accounts.lastTwo.map((f) => `${f.year} ${f.fact === 'read' ? (fr ? 'comptes lus' : 'accounts read') : f.fact === 'withheld' ? (fr ? 'déposés sous confidentialité' : 'filed under confidentiality') : (fr ? 'non déposés' : 'not filed')}`).join(fr ? ' ; ' : '; ')}.`
    : accounts.kind === 'confidential'
      ? fr
        ? `Les comptes sont déposés avec la déclaration de confidentialité que la loi permet (${accounts.withheld.join(', ')}) : ils existent mais nul ne peut les consulter.`
        : `The accounts are filed with the confidentiality declaration the law allows (${accounts.withheld.join(', ')}): they exist, and no one may read them.`
      : fr
        ? `Société trop récente pour avoir déposé deux exercices.`
        : `The company is too young to have filed two years of accounts.`;
  return (
    <div className="mt-3 max-w-3xl rounded-[20px] border border-[#262626] bg-[#1B1B1B]">
      <div className="flex items-center justify-between px-5 pb-1 pt-4">
        <span className="font-medium text-white" style={{ fontSize: 14 }}>{fr ? 'Pourquoi pas de comptes récents' : 'Why no recent accounts'}</span>
        <span className="text-[#8E8E8E]" style={{ fontSize: 12 }}>{fr ? 'registre officiel' : 'official register'}</span>
      </div>
      <div className="flex flex-col gap-3 px-5 pb-4 pt-1">
        {accounts.kind === 'young' && accounts.founded ? (
          <div>
            <div className="text-[#8E8E8E]" style={{ fontSize: 12 }}>{fr ? 'Fondée le' : 'Founded on'}</div>
            <div className="font-medium text-white" style={{ fontSize: 18 }}>{longDate(accounts.founded, fr)}</div>
          </div>
        ) : null}
        <p className="text-[#E8E8E8]" style={{ fontSize: 13 }}>{lead}</p>
        {accounts.finding ? <p className="text-[#B4B4B4]" style={{ fontSize: 13 }}>{fr ? accounts.finding.fr : accounts.finding.en}</p> : null}
        <div className="flex flex-col">
          {accounts.companies.map((c) => (
            <div key={c.siren} className="flex flex-wrap items-baseline justify-between gap-x-3 border-t border-[#262626] py-2">
              <span className="text-[#ECECEC]" style={{ fontSize: 12 }}>
                <a href={`https://annuaire-entreprises.data.gouv.fr/entreprise/${c.siren}`} target="_blank" rel="noreferrer" className="underline decoration-[#3A3A3A] underline-offset-2 hover:text-white">{c.name}</a>
                <span className="text-[#8E8E8E]">{` · ${c.siren}${c.parent ? (fr ? ' · société mère' : ' · parent company') : ''}`}</span>
              </span>
              <span className="text-[#8E8E8E]" style={{ fontSize: 12 }}>
                {[c.state === 'C' ? (fr ? `radiée${c.closedOn ? ` le ${longDate(c.closedOn, fr)}` : ''}` : `struck off${c.closedOn ? ` on ${longDate(c.closedOn, fr)}` : ''}`) : (fr ? 'active' : 'active'),
                  c.created ? (fr ? `créée en ${c.created.slice(0, 4)}` : `founded ${c.created.slice(0, 4)}`) : null,
                  typeof c.openSites === 'number' ? (fr ? `${c.openSites} établissement${c.openSites > 1 ? 's' : ''} ouvert${c.openSites > 1 ? 's' : ''}` : `${c.openSites} open site${c.openSites === 1 ? '' : 's'}`) : null,
                  c.filings.length ? (fr ? `dépôts : ${c.filings.slice(0, 5).map((f) => `${f.year}${f.confidential ? ' (conf.)' : ''}`).join(', ')}` : `filed: ${c.filings.slice(0, 5).map((f) => `${f.year}${f.confidential ? ' (conf.)' : ''}`).join(', ')}`) : (fr ? 'aucun dépôt' : 'nothing filed')]
                  .filter(Boolean).join(' · ')}
              </span>
            </div>
          ))}
        </div>
        {accounts.finding?.sources.length ? (
          <div className="flex flex-wrap gap-x-3 text-[#8E8E8E]" style={{ fontSize: 12 }}>
            {accounts.finding.sources.map((src) => (
              <a key={src.url} href={src.url} target="_blank" rel="noreferrer" className="underline decoration-[#3A3A3A] underline-offset-2 hover:text-[#9A9A9A]">{src.label}</a>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const BrandRevenueChart = ({ brandId, lang }: { brandId?: string; lang: string }) => {
  const fr = lang === 'fr';
  // Verified per-year records only. The denormalised Brand.sortableTotalRevenue is deliberately
  // NOT read here: it is one number with no period, and one number cannot be a history.
  const records = useKernelStore((state) =>
    brandId ? state.inventory.Brand.registry[brandId]?.verifiedFinancials ?? null : null
  );
  // ONE series in ONE currency. A brand can hold French EUR filings AND its listed group's USD
  // filings; a line mixing the two would connect numbers in different units. The currency with
  // the most filed years is the one with a history to show, EUR winning ties.
  const [metric, setMetric] = useState<'revenue' | 'profit'>('revenue');
  const { points, rows, currency, entityName } = useMemo(() => {
    const usable = (records ?? []).filter(
      (r) => typeof r.revenue === 'number' && r.revenue > 0 && typeof r.year === 'number'
    );
    const byCurrency = new Map<string, typeof usable>();
    for (const r of usable) {
      const code = (r.currency ?? 'EUR').toUpperCase();
      byCurrency.set(code, [...(byCurrency.get(code) ?? []), r]);
    }
    const winner = [...byCurrency.entries()].sort(
      (a, b) => b[1].length - a[1].length || (a[0] === 'EUR' ? -1 : 1) - (b[0] === 'EUR' ? -1 : 1)
    )[0];
    const rows = (winner?.[1] ?? []).sort((a, b) => a.year - b.year);
    return {
      points: rows.map((r) => ({ x: String(r.year), y: r.revenue as number })),
      rows,
      currency: winner?.[0] ?? 'EUR',
      entityName: rows.find((r) => r.entityName)?.entityName ?? null,
    };
  }, [records]);
  const declaredRows = useKernelStore((state) =>
    brandId ? state.inventory.Brand.registry[brandId]?.declaredFinancials ?? null : null
  );
  const founded = useKernelStore((state) => {
    const accounts = brandId ? state.inventory.Brand.registry[brandId]?.registerAccounts : null;
    return accounts?.kind === 'young' ? accounts.founded : null;
  });
  const declared = useMemo(() => pickDeclaredSeries(declaredRows), [declaredRows]);
  const axisLeft = useMemo(() => axisLeftFor(currency), [currency]);
  const tooltip = useMemo(() => revenueTooltipFor(currency), [currency]);
  const declaredAxisLeft = useMemo(() => (declared ? axisLeftFor(declared.currency) : null), [declared]);
  const declaredTooltip = useMemo(() => (declared ? revenueTooltipFor(declared.currency) : null), [declared]);
  const declaredSeries = useMemo(
    () => (declared ? [{ id: 'declared', data: declared.points }] : null),
    [declared],
  );
  // Same identity-stability reason as the hoisted constants above: Nivo re-derives the whole chart
  // when `data` changes identity, and `points` is already memoised on the records.
  // Each year with its growth on the year before (only a consecutive one) and its margin.
  const years = useMemo(() => rows.map((r, i) => {
    const prev = rows[i - 1];
    const revenue = r.revenue as number;
    const netIncome = typeof r.netIncome === 'number' ? r.netIncome : null;
    return {
      year: r.year, revenue, netIncome, sourceUrl: r.sourceUrl,
      growth: prev && prev.year === r.year - 1 ? revenue / (prev.revenue as number) - 1 : null,
      margin: netIncome === null ? null : netIncome / revenue,
    };
  }), [rows]);
  const profitPoints = useMemo(() => years.filter((y) => y.netIncome !== null).map((y) => ({ x: String(y.year), y: y.netIncome as number })), [years]);
  const showProfit = metric === 'profit' && profitPoints.length >= 2;
  const series = useMemo(() => [{ id: showProfit ? 'profit' : 'revenue', data: showProfit ? profitPoints : points }], [showProfit, profitPoints, points]);

  // One point is not a trend. Nivo would happily draw a single dot and an area under it, which
  // reads as a shape when it is a fact - so a lone filed year is stated in words instead.
  if (points.length < 2) {
    const only = points[0];
    return (
      <>
      {declared && declaredSeries && declaredAxisLeft && declaredTooltip ? (
        // THE DECLARED CURVE: dashed, grey, headed by its source - a cited claim drawn, never a
        // fact asserted. It exists so the answer to "where is the curve?" is never a blank box
        // when official figures are published somewhere no register we read can reach.
        <div className="max-w-3xl overflow-hidden rounded-[20px] border border-[#262626] bg-[#1B1B1B]">
          <div className="flex items-center justify-between px-5 pb-1 pt-4">
            <span className="font-medium text-white" style={{ fontSize: 14 }}>
              {fr ? 'Chiffre d’affaires déclaré' : 'Declared revenue'}
            </span>
            <span className="text-[#8E8E8E]" style={{ fontSize: 12 }}>
              {fr
                ? `non vérifié · ${declared.source} · ${declared.currency}`
                : `unverified · ${declared.source} · ${declared.currency}`}
            </span>
          </div>
          <div className="h-[220px] w-full p-2">
            <ChartErrorBoundary label="Declared revenue">
            <ResponsiveLine
              data={declaredSeries}
              margin={CHART_MARGIN}
              xScale={X_SCALE}
              yScale={Y_SCALE}
              curve="monotoneX"
              colors={DECLARED_LINE_COLORS}
              enableArea={false}
              enablePoints
              pointSize={6}
              pointColor="#141414"
              pointBorderWidth={2}
              pointBorderColor={DECLARED_LINE_COLOR}
              enableGridX={false}
              axisBottom={AXIS_BOTTOM}
              axisLeft={declaredAxisLeft}
              theme={CHART_THEME}
              tooltip={declaredTooltip}
              layers={DECLARED_LAYERS}
              isInteractive
              useMesh
              animate={false}
            />
            </ChartErrorBoundary>
          </div>
        </div>
      ) : (
      <div className="flex h-[220px] flex-col items-center justify-center gap-1 rounded-[20px] border border-[#262626] bg-[#1B1B1B] px-6 text-center">
        <span className="text-[#B4B4B4]" style={{ fontSize: 13 }}>
          {only
            ? fr
              ? `Un seul exercice publié : ${only.x}, ${formatRevenue(only.y, currency)}`
              : `One filed year: ${only.x}, ${formatRevenue(only.y, currency)}`
            : fr
              ? 'Aucun chiffre d’affaires vérifié.'
              : 'No verified revenue.'}
        </span>
        <span className="text-[#8E8E8E]" style={{ fontSize: 12 }}>
          {fr
            ? 'Pas assez d’exercices publiés pour tracer une courbe.'
            : 'Not enough filed years to draw a history.'}
        </span>
        {founded ? (
          <span className="text-[#B4B4B4]" style={{ fontSize: 12 }}>
            {fr ? `Société fondée le ${longDate(founded, fr)}` : `Company founded on ${longDate(founded, fr)}`}
          </span>
        ) : null}
      </div>
      )}
      {/* ONLY WHEN THERE IS NOTHING FILED. An estimate exists to fill a gap, so it disappears the
          moment the gap is filled - the same rule the identity fiche already follows, and for the
          same reason: "a reader offered two revenues picks the bigger one and forgets which was
          which". Zara made the cost concrete: filed EUR 1.89bn above, modelled EUR 2m below,
          which is an invitation to quote the wrong one by a factor of a thousand. */}
      <GroupRevenue brandId={brandId} lang={lang} />
      <DeclaredFinancials brandId={brandId} lang={lang} />
      <RegisterAccountsNotice brandId={brandId} lang={lang} />
      {/* The modelled-estimate block is HIDDEN, not deleted (owner's call, 2026-09-19: the panel
          shows filed and declared figures only). BrandEstimateHypotheses and the estimate rows it
          reads stay intact - the scoring pipeline still uses the model - so un-hiding is one line. */}
      </>
    );
  }

  // points.length >= 2 here, so both ends exist.
  const first = years[0]!;
  const last = years[years.length - 1]!;
  const span = last.year - first.year;
  const cagr = span > 0 ? Math.pow(last.revenue / first.revenue, 1 / span) - 1 : null;
  const money = (v: number | null) => (v === null ? '–' : signedRevenue(v, currency) ?? '–');
  const pill = (on: boolean) => `h-8 rounded-full px-3 transition-colors ${on ? 'bg-white/[0.12] text-white' : 'text-[#AFAFAF] hover:bg-white/[0.08] hover:text-white'}`;

  return (
    <div className="@container max-w-3xl">
    <div className="grid grid-cols-2 gap-2 @[560px]:grid-cols-4">
      <Stat label={fr ? `Chiffre d’affaires ${last.year}` : `Revenue ${last.year}`} value={money(last.revenue)} />
      <Stat label={fr ? 'Sur un an' : 'Year on year'} value={percent(last.growth)} note={last.growth === null ? undefined : `vs ${last.year - 1}`} />
      <Stat label={fr ? 'Croissance par an' : 'Growth per year'} value={percent(cagr)} note={fr ? `moyenne ${first.year}–${last.year}` : `average ${first.year}–${last.year}`} />
      <Stat label={fr ? `Résultat net ${last.year}` : `Net profit ${last.year}`} value={money(last.netIncome)} note={last.margin === null ? undefined : (fr ? `marge ${(last.margin * 100).toFixed(1).replace('-', '−')} %` : `${(last.margin * 100).toFixed(1).replace('-', '−')}% margin`)} />
    </div>
    <div className="mt-3 overflow-hidden rounded-[20px] border border-[#262626] bg-[#1B1B1B]">
      <div className="flex items-center justify-between gap-3 px-5 pb-1 pt-4">
        {profitPoints.length >= 2 ? (
          <div className="-ml-2 flex items-center gap-1" role="tablist">
            <button type="button" role="tab" aria-selected={!showProfit} onClick={() => setMetric('revenue')} className={pill(!showProfit)} style={{ fontSize: 13 }}>{fr ? 'Chiffre d’affaires' : 'Revenue'}</button>
            <button type="button" role="tab" aria-selected={showProfit} onClick={() => setMetric('profit')} className={pill(showProfit)} style={{ fontSize: 13 }}>{fr ? 'Résultat net' : 'Net profit'}</button>
          </div>
        ) : (
          <span className="font-medium text-white" style={{ fontSize: 14 }}>{fr ? 'Chiffre d’affaires' : 'Revenue'}</span>
        )}
        <span className="truncate text-[#8E8E8E]" style={{ fontSize: 12 }}>
          {/* A non-EUR series is the GROUP's accounts, and the reader must not take it for the
              France figure: name whose accounts and in what unit, in the header, not a tooltip. */}
          {currency !== 'EUR' && entityName
            ? fr
              ? `${points.length} exercices publiés · comptes du groupe ${entityName} · ${currency}`
              : `${points.length} filed years · group accounts, ${entityName} · ${currency}`
            : fr
              ? `${points.length} exercices publiés`
              : `${points.length} filed years`}
        </span>
      </div>
      <div className="h-[300px] w-full p-2">
        <ChartErrorBoundary label="Revenue">
        <ResponsiveLine
          data={series}
          margin={CHART_MARGIN}
          xScale={X_SCALE}
          yScale={showProfit ? PROFIT_Y_SCALE : Y_SCALE}
          curve="monotoneX"
          colors={LINE_COLORS}
          lineWidth={2}
          enableArea={!showProfit}
          areaOpacity={0.12}
          enablePoints
          pointSize={7}
          pointColor="#141414"
          pointBorderWidth={2}
          pointBorderColor={LINE_COLOR}
          enableGridX={false}
          gridYValues={4}
          axisBottom={AXIS_BOTTOM}
          axisLeft={axisLeft}
          isInteractive
          useMesh
          theme={CHART_THEME}
          tooltip={tooltip}
        />
        </ChartErrorBoundary>
      </div>
    </div>
    {/* Every filed year, newest first: the figures behind the curve, each with its filing. */}
    <div className="mt-3 overflow-hidden rounded-[20px] border border-[#262626] bg-[#1B1B1B] px-2 py-2">
      <table className="w-full table-fixed @max-[480px]:[&_td]:px-1.5 @max-[480px]:[&_td]:whitespace-nowrap @max-[480px]:[&_th]:px-1.5 @max-[480px]:[&_th]:whitespace-nowrap">
        <thead>
          <tr className="text-left text-[#8E8E8E]" style={LABEL}>
            <th className="px-3 py-2 font-normal">{fr ? 'Exercice' : 'Year'}</th>
            <th className="px-3 py-2 text-right font-normal">{fr ? 'Chiffre d’affaires' : 'Revenue'}</th>
            <th className="px-3 py-2 text-right font-normal">{fr ? 'Évolution' : 'Growth'}</th>
            <th className="px-3 py-2 text-right font-normal">{fr ? 'Résultat net' : 'Net profit'}</th>
            <th className="px-3 py-2 text-right font-normal @max-[480px]:hidden">{fr ? 'Marge' : 'Margin'}</th>
            <th className="w-[72px] px-3 py-2 text-right font-normal">Source</th>
          </tr>
        </thead>
        <tbody>
          {[...years].reverse().map((y) => (
            <tr key={y.year} className="border-t border-[#262626] text-[#ECECEC]" style={CELL}>
              <td className="px-3 py-2.5">{y.year}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{money(y.revenue)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-[#AFAFAF]">{percent(y.growth)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{money(y.netIncome)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-[#AFAFAF] @max-[480px]:hidden">{y.margin === null ? '–' : `${(y.margin * 100).toFixed(1).replace('-', '−')}%`}</td>
              <td className="px-3 py-2.5 text-right">
                {y.sourceUrl ? <a href={y.sourceUrl} target="_blank" rel="noreferrer" className="text-[#AFAFAF] underline decoration-[#444] underline-offset-2 hover:text-white">{fr ? 'Dépôt' : 'Filing'}</a> : <span className="text-[#8E8E8E]">–</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {entityName ? (
        <p className="border-t border-[#262626] px-3 pb-1 pt-2.5 text-[#8E8E8E]" style={LABEL}>
          {fr ? `Comptes déposés de ${entityName}.` : `Filed accounts of ${entityName}.`}
        </p>
      ) : null}
    </div>
    <GroupRevenue brandId={brandId} lang={lang} />
    <DeclaredFinancials brandId={brandId} lang={lang} />
    <RegisterAccountsNotice brandId={brandId} lang={lang} />
    </div>
  );
};
